import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export interface CartItem {
  menuItemId: string;
  name: string;
  price: number;
  quantity: number;
  specialInstructions?: string;
  imageUrl?: string;
}

export interface CartStore {
  storeId: string;
  storeCode: string;
  storeName: string;
  enterpriseId: string;
}

interface CartContextType {
  items: CartItem[];
  store: CartStore | null;
  itemCount: number;
  subtotal: number;
  addItem: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  removeItem: (menuItemId: string) => void;
  updateQuantity: (menuItemId: string, quantity: number) => void;
  updateSpecialInstructions: (menuItemId: string, instructions: string) => void;
  clearCart: () => void;
  setStore: (store: CartStore) => void;
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'bso_cart';
const STORE_STORAGE_KEY = 'bso_cart_store';

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [store, setStoreState] = useState<CartStore | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      const savedStore = localStorage.getItem(STORE_STORAGE_KEY);

      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        if (Array.isArray(parsedCart)) {
          setItems(parsedCart);
        }
      }

      if (savedStore) {
        const parsedStore = JSON.parse(savedStore);
        if (parsedStore && parsedStore.storeId) {
          setStoreState(parsedStore);
        }
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [items]);

  // Save store to localStorage whenever it changes
  useEffect(() => {
    try {
      if (store) {
        localStorage.setItem(STORE_STORAGE_KEY, JSON.stringify(store));
      } else {
        localStorage.removeItem(STORE_STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error saving store to localStorage:', error);
    }
  }, [store]);

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const addItem = useCallback((newItem: Omit<CartItem, 'quantity'>, quantity: number = 1) => {
    setItems(currentItems => {
      const existingIndex = currentItems.findIndex(item => item.menuItemId === newItem.menuItemId);

      if (existingIndex >= 0) {
        // Item exists, update quantity
        const updatedItems = [...currentItems];
        updatedItems[existingIndex] = {
          ...updatedItems[existingIndex],
          quantity: updatedItems[existingIndex].quantity + quantity
        };
        return updatedItems;
      } else {
        // New item
        return [...currentItems, { ...newItem, quantity }];
      }
    });
  }, []);

  const removeItem = useCallback((menuItemId: string) => {
    setItems(currentItems => currentItems.filter(item => item.menuItemId !== menuItemId));
  }, []);

  const updateQuantity = useCallback((menuItemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(menuItemId);
      return;
    }

    setItems(currentItems =>
      currentItems.map(item =>
        item.menuItemId === menuItemId
          ? { ...item, quantity }
          : item
      )
    );
  }, [removeItem]);

  const updateSpecialInstructions = useCallback((menuItemId: string, instructions: string) => {
    setItems(currentItems =>
      currentItems.map(item =>
        item.menuItemId === menuItemId
          ? { ...item, specialInstructions: instructions }
          : item
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setStoreState(null);
    localStorage.removeItem(CART_STORAGE_KEY);
    localStorage.removeItem(STORE_STORAGE_KEY);
  }, []);

  const setStore = useCallback((newStore: CartStore) => {
    // If switching stores, clear the cart
    if (store && store.storeId !== newStore.storeId) {
      setItems([]);
    }
    setStoreState(newStore);
  }, [store]);

  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);
  const toggleCart = useCallback(() => setIsCartOpen(prev => !prev), []);

  const value: CartContextType = {
    items,
    store,
    itemCount,
    subtotal,
    addItem,
    removeItem,
    updateQuantity,
    updateSpecialInstructions,
    clearCart,
    setStore,
    isCartOpen,
    openCart,
    closeCart,
    toggleCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export default CartContext;
